import React from 'react';

import { Box, SimpleGrid, Stack, Text, createStyles } from '@mantine/core';
import dayjs from 'dayjs';

import { hasWorkout } from './calendarDay';
import AssignWorkoutSlot from './cards/AssignWorkoutSlot';
import WeekCard from './cards/WeekCard';
import type { AthleteCalendarItem, AthleteCalendarWorkoutItem } from './types';

const COLUMN_MIN_HEIGHT = 460;

const useStyles = createStyles((theme, { isToday }: { isToday: boolean }) => ({
  column: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: COLUMN_MIN_HEIGHT,
    minWidth: 0,
    border: `1px solid ${theme.colors.gray[2]}`,
    borderRadius: theme.radius.md,
    overflow: 'hidden',
    backgroundColor: theme.white,
  },
  header: {
    padding: theme.spacing.sm,
    borderBottom: `1px solid ${theme.colors.gray[2]}`,
    backgroundColor: isToday ? theme.colors.kiloBlue[6] : theme.colors.gray[0],
    color: isToday ? theme.white : theme.colors.gray[7],
  },
  body: { flex: 1, overflowY: 'auto', padding: theme.spacing.xs },
}));

interface AthleteDayColumnProps {
  date: string;
  items: AthleteCalendarItem[];
  isToday: boolean;
  onItemClick: (item: AthleteCalendarItem, date: string) => void;
  onAssignWorkout: (date: string) => void;
  canAssign: boolean;
  renderWorkoutMenu?: (item: AthleteCalendarWorkoutItem, date: string) => React.ReactNode;
}

const AthleteDayColumn = ({
  date,
  items,
  isToday,
  onItemClick,
  onAssignWorkout,
  canAssign,
  renderWorkoutMenu,
}: AthleteDayColumnProps): JSX.Element => {
  const { classes } = useStyles({ isToday });
  const day = dayjs(date);

  return (
    <Box className={classes.column} data-cy='athleteWeekColumn'>
      <Box className={classes.header}>
        <Text size='xs' fw={600}>
          {day.format('ddd')}
        </Text>
        <Text size='lg' fw={700}>
          {day.format('D')}
        </Text>
      </Box>
      <Box className={classes.body}>
        <Stack spacing='xs'>
          {items.map(item => (
            <WeekCard
              key={`${item.kind}-${item.id}`}
              item={item}
              onClick={clicked => onItemClick(clicked, date)}
              menu={item.kind === 'workout' ? renderWorkoutMenu?.(item, date) : undefined}
            />
          ))}
          {canAssign && !hasWorkout(items) && (
            <AssignWorkoutSlot onClick={() => onAssignWorkout(date)} variant='inline' />
          )}
        </Stack>
      </Box>
    </Box>
  );
};

interface AthleteWeekViewProps {
  weekStart: string;
  itemsByDate: Map<string, AthleteCalendarItem[]>;
  onItemClick: (item: AthleteCalendarItem, date: string) => void;
  onAssignWorkout: (date: string) => void;
  canAssign: boolean;
  renderWorkoutMenu?: (item: AthleteCalendarWorkoutItem, date: string) => React.ReactNode;
}

const AthleteWeekView = ({
  weekStart,
  itemsByDate,
  onItemClick,
  onAssignWorkout,
  canAssign,
  renderWorkoutMenu,
}: AthleteWeekViewProps): JSX.Element => {
  const today = dayjs().format('YYYY-MM-DD');
  const days = Array.from({ length: 7 }, (_, index) =>
    dayjs(weekStart).add(index, 'day').format('YYYY-MM-DD'),
  );

  return (
    <SimpleGrid cols={7} spacing='xs' p='sm' data-cy='athleteWeekGrid'>
      {days.map(day => (
        <AthleteDayColumn
          key={day}
          date={day}
          items={itemsByDate.get(day) ?? []}
          isToday={day === today}
          onItemClick={onItemClick}
          onAssignWorkout={onAssignWorkout}
          canAssign={canAssign}
          renderWorkoutMenu={renderWorkoutMenu}
        />
      ))}
    </SimpleGrid>
  );
};

export default AthleteWeekView;
